import { FC, useEffect, useState } from 'react'
import { noop } from 'rxjs'
import { useOpenFin } from '../../../helpers/hooks/useOpenFin'
import { confirmOrder, rejectOrder } from '../../../store/lastLook/actions'
import { SpotCrossSelection } from '../../../store/lastLook/types'
import { addLogItem } from '../../../store/log/actions'
import { getUserOrOperatorOrderById } from '../../../store/order/selectors'
import { getSecurityStaticDataById } from '../../../store/securities/selectors'
import {
  addOrUpdateStagedOrders,
  removeStagedOrder
} from '../../../store/stagedOrders/actions'
import { getStagedOrderBySecurityForOrderType } from '../../../store/stagedOrders/selectors'
import { useAppDispatch, useAppSelector } from '../../../store/types'
import { removeLastLookFromWindows } from '../../../store/windows/actions'
import { LastLookWindowInfo } from '../../../store/windows/types'
import { useSecurityModalInfo } from '../AggressorModal/helpers'
import LastLookModalFooter from '../LastLookModalFooter/LastLookModalFooter'
import HeaderModal from '../ModalHeader/ModalHeader'
import ModalSecurityInformationWrapper from '../ModalSecurityInformationWrapper'
import LastLookDepth from './LastLookDepth'
import LastLookModalContent from './LastLookModalContent'

import styles from '../modal.module.scss'

interface Props {
  windowInfo: LastLookWindowInfo
  index?: number
}

const LastLookModal: FC<Props> = ({ windowInfo, index = 0 }) => {
  const { finWindowAction } = useOpenFin()
  const [logWritten, setLogWritten] = useState<boolean>(false)
  const [size, setSize] = useState(0)
  const securityModalInfo = useSecurityModalInfo(windowInfo.orderId, size)
  const getSecurity = useAppSelector(getSecurityStaticDataById)
  const getOrder = useAppSelector(getUserOrOperatorOrderById)
  const getStagedOrder = useAppSelector(getStagedOrderBySecurityForOrderType)
  const dispatch = useAppDispatch()
  const [spotCrossSelection, setSpotCrossSelection] =
    useState<SpotCrossSelection>('S')

  const security =
    securityModalInfo && getSecurity(securityModalInfo.securityId)

  const order = securityModalInfo && getOrder(windowInfo.orderId)

  const aggressorOrder = order?.aggressorOrder
  const orderStatus = aggressorOrder?.status
  const tradeAmt = aggressorOrder?.size
  const minSizeForTsyHedge = securityModalInfo?.minSizeForTsyHedge
    ? securityModalInfo.minSizeForTsyHedge
    : '500'

  useEffect(() => {
    // check user record to determine which autospot/cross button to select on load
    if (securityModalInfo && tradeAmt && minSizeForTsyHedge) {
      if (
        securityModalInfo.spotHedgePreference.startsWith('H') &&
        securityModalInfo.benchmarkPrice &&
        order?.isSpreadOrder
      ) {
        setSpotCrossSelection('H')
      }
      if (tradeAmt < Number(minSizeForTsyHedge)) {
        setSpotCrossSelection('S')
      }
    }
  }, [securityModalInfo, aggressorOrder, order])

  useEffect(() => {
    if (!logWritten && securityModalInfo) {
      const st = `Last look popup for ${security?.boardLabel} - ${
        order?.type
      } ${String(tradeAmt)} @ ${securityModalInfo?.price}`
      dispatch(addLogItem(st))
      setLogWritten(true)
    }
  }, [securityModalInfo])

  useEffect(() => {
    setSize(order?.aggressorOrder?.size || 0)
  }, [order?.aggressorOrder?.size])

  if (!security || !securityModalInfo || !order) {
    return null
  }

  const borderColor = `var(--${order.type}SecondaryColor)`

  const handleCloseModal = () => {
    dispatch(removeLastLookFromWindows(windowInfo))
    finWindowAction(`LastLook/${windowInfo.orderId}`, 'close')
  }

  const handleConfirmButtonClick = (tradeAmount?: number) => () => {
    if (order?.id) {
      dispatch(confirmOrder(order.id, spotCrossSelection))

      const stagedOrder = getStagedOrder(order.securityId, order.type)
      if (
        stagedOrder &&
        tradeAmount &&
        tradeAmount !== Math.max(stagedOrder.size, stagedOrder.totalSize ?? 0)
      ) {
        const { size: orderSize, totalSize = 0, ...rest } = stagedOrder
        const isIb = totalSize > orderSize
        const newSize = (totalSize ?? orderSize) - tradeAmount
        const overrides = isIb
          ? { totalSize: newSize, size: orderSize }
          : { size: newSize }
        dispatch(
          addOrUpdateStagedOrders([
            {
              ...rest,
              ...overrides
            }
          ])
        )
      } else {
        dispatch(
          removeStagedOrder({
            securityId: order.securityId,
            orderType: order.type
          })
        )
      }
    } else {
      console.error('orderId not found')
    }
  }

  const handleRejectButtonClick = () => {
    if (order?.id) {
      dispatch(rejectOrder(order.id))
    } else {
      console.error('orderId not found')
    }
  }

  return (
    <div className={styles.modalWrapper}>
      <section
        data-testid="lastlook-modal"
        className={styles.modalMain}
        style={{ borderColor }}
      >
        <HeaderModal
          type={order.type}
          security={security}
          showSecurityInfo={!!securityModalInfo}
          issue={securityModalInfo?.issue}
          showMinimize={false}
          index={index}
          isActiveWindow={true}
          handleMinimize={noop}
          orderStatusComp={aggressorOrder?.status}
        />

        <>
          <LastLookModalContent
            order={order}
            securityModalInfo={securityModalInfo}
            price={securityModalInfo.price}
            styles={styles}
            tradeAmt={String(tradeAmt)}
            type={securityModalInfo.type}
            setSpotCrossSelection={setSpotCrossSelection}
            spotCrossSelection={spotCrossSelection}
            orderStatusComp={orderStatus}
            currency={security.currency}
            isTreasury={security.product === 'PrinUSGovtOutright'}
          />

          <ModalSecurityInformationWrapper
            securityModalInfo={securityModalInfo}
            security={security}
          />

          <LastLookDepth security={security} />

          <LastLookModalFooter
            orderId={order.id}
            orderStatusComp={orderStatus}
            size={order.size}
            type={order.type}
            tradeAmt={Number(tradeAmt)}
            actionCloseModal={handleCloseModal}
            handleConfirmButtonClick={handleConfirmButtonClick}
            handleRejectButtonClick={handleRejectButtonClick}
            benchmarkName={security.benchmarkName}
            spotCrossSelection={spotCrossSelection}
          />
        </>
      </section>
    </div>
  )
}

export default LastLookModal
